import React from 'react'
import clsx from 'clsx'
import { Record } from 'immutable'
import PropTypes from 'prop-types'
import { useTranslation } from '@sm360/hooks'
import { ListingContext } from '../../configs/listing-view/state.manager'
import FilterOption from '../filter-option/FilterOption'
import FilterSection from '../filter-section/FilterSection'
import { changeUrl } from '../../utils/handle-url.utils'
import ga4Trigger from '../../datalayer/analytics.service'
import dataLayerTags, { FILTER_MAP } from '../../constants/dataLayerTags'
import { getPathname, isArray, isEmptyObject } from '../../utils/helper.utils'

// name the filters that allow to display count
const filtersAllowCount = ['makeId', 'modelId', 'year']

const FilterSections = ({ extraClasses }) => {
    const {
        state: { filterMetaData, vehicleCriteria, hubName, vehicles, filters },
        addVehicleCriteria,
        dispatch,
    } = React.useContext(ListingContext)
    const [activeFilter, setActiveFilter] = React.useState({})
    const {
        i18n: { language },
    } = useTranslation()

    React.useEffect(() => {
        const selectedCodes = activeFilter?.metadatas?.filter((item) => item.isSelected).map((item) => item.code)

        const allSelectedInCriteria = selectedCodes?.every((code) => vehicleCriteria?.[activeFilter.name]?.includes(code))

        if (!allSelectedInCriteria || isEmptyObject(vehicleCriteria)) setActiveFilter({})
    }, [vehicleCriteria])

    const baseClass = 'il-filterSections'
    const classNames = clsx(baseClass, extraClasses)

    const triggerAnalytics = (attribute, label, isSelected) => {
        ga4Trigger(dataLayerTags.LAYER_NAVIGATION__FILTERS(getPathname(), FILTER_MAP(attribute)))
        const gaOptions = dataLayerTags.FILTER_OPTIONS(attribute, label)
        if (gaOptions && !isSelected) ga4Trigger(gaOptions)
    }

    const triggerRangeAnalytics = (attribute, value) => {
        ga4Trigger(dataLayerTags.LAYER_NAVIGATION__FILTERS(getPathname(), FILTER_MAP(attribute)))
        const gaOptions = dataLayerTags.FILTER_OPTIONS(attribute, value)
        if (gaOptions) ga4Trigger(gaOptions)
    }

    const handleChange = (attribute, { code, label, isSelected }, metadatas) => {
        const selectedIndex = metadatas.findIndex((metadata) => metadata.code === code)
        if (Record.isRecord(metadatas[selectedIndex])) {
            metadatas[selectedIndex] = metadatas[selectedIndex].set('isSelected', !metadatas[selectedIndex].isSelected)
        } else {
            metadatas[selectedIndex].isSelected = !metadatas[selectedIndex].isSelected
        }

        // Recalculate criteria
        const criteria = addVehicleCriteria(vehicleCriteria, metadatas, attribute, true)

        // Determine if we need to update the active filter
        const shouldSetActiveFilter = activeFilter && activeFilter.name !== attribute
        // Determine if no filter options selected for current filter
        const isEmptyOptionSelection = !criteria?.[attribute]?.length

        if (shouldSetActiveFilter) {
            const newActiveFilter = filterMetaData?.find((meta) => meta.name === attribute)
            setActiveFilter(newActiveFilter)
        }

        if (isEmptyOptionSelection) {
            // If no option selected for the current filter then set active filter as an empty to display all available options
            setActiveFilter({})
        }

        changeUrl(criteria, filterMetaData, language, hubName)
        dispatch({ type: 'updateVehicleCriteria', vehicleCriteria: criteria })
        triggerAnalytics(attribute, label, isSelected)
    }

    const handleInputRange = (name, value, attribute) => {
        const criteria = addVehicleCriteria(vehicleCriteria, { [name]: value }, null, true)
        changeUrl(criteria, filterMetaData, language, hubName)
        dispatch({ type: 'updateVehicleCriteria', vehicleCriteria: criteria })
        triggerRangeAnalytics(attribute, value)
    }
    const isSectionOpen = (name) => Object.keys(vehicleCriteria).includes(name)

    /**
     * Function to check if itemId (modelId/trimId) is relevant to selected parent item (make or model)
     * @param {*} parentItem // For model -> make, trim -> model
     * @param {*} parentItemToValidate // For modelId -> makeIdAndModelId, for trimId -> modelId
     * @param {*} currentItemId // either modelId or trimId for now
     * @returns boolen to indicate if itemId is relevant to selected parent item or not
     */
    const isItemRelevantToParentItem = (parentItem, parentItemToValidate, currentItemId) => {
        const isParentItemSelected = Object.keys(vehicleCriteria).includes(parentItem) && vehicleCriteria?.[parentItem].length > 0
        const selectedParentItems = isParentItemSelected ? vehicleCriteria?.[parentItem] : []

        if (!isParentItemSelected || !isArray(filters?.[parentItemToValidate])) return true

        return filters?.[parentItemToValidate]
            .filter(({ key }) => selectedParentItems.includes(key)) // find selected parentId
            .map(({ includedItemsValueKeys }) => includedItemsValueKeys) // keep only array of child
            .flat() // flatten final array with only child models
            .includes(currentItemId) // check if curentId is in final array of available parents includedItems
    }

    const isModel = (name) => {
        return name === 'modelId'
    }

    const isTrim = (name) => {
        return name === 'trimId'
    }

    const modifiedFilterData = filterMetaData.map((metadata) =>
        metadata.name === activeFilter.name && !isEmptyObject(vehicleCriteria) ? { ...metadata, ...activeFilter } : metadata
    )
    return (
        <div className={classNames}>
            {modifiedFilterData &&
                modifiedFilterData.length > 0 &&
                modifiedFilterData
                    ?.filter(({ label }) => label !== undefined)
                    .map(({ name, label, metadatas }) => (
                        <FilterSection key={name} label={label[language]} isOpen={isSectionOpen(name)}>
                            {metadatas &&
                                metadatas.length > 0 &&
                                metadatas.map((metadata) => {
                                    const { code, count, type, label, isSelected, min, max } = metadata
                                    const displayCount = count > 0 || filtersAllowCount.includes(name)

                                    // don't show unselected models if a make is selected
                                    if (
                                        isModel(name) &&
                                        !isSelected &&
                                        !isItemRelevantToParentItem('makeId', 'makeIdAndModelId', code)
                                    ) {
                                        return null
                                    }
                                    // Show only relevant trim to the selected model
                                    if (isTrim(name) && !isSelected && !isItemRelevantToParentItem('modelId', 'modelId', code)) {
                                        return null
                                    }

                                    return (
                                        <FilterOption
                                            key={`${code} | ${label}`}
                                            // TODO: Remove Number to string conversion once get the final fitler metadata
                                            label={typeof label === 'string' ? label : label.toString()}
                                            count={count}
                                            type={type}
                                            handleChange={() => handleChange(name, metadata, metadatas)}
                                            isSelected={isSelected}
                                            handleInputRange={(inputName, value) => handleInputRange(inputName, value, name)}
                                            vehicleCriteria={vehicleCriteria}
                                            min={parseInt(min)}
                                            max={parseInt(max)}
                                            filterName={name}
                                            displayCount={displayCount}
                                        />
                                    )
                                })}
                        </FilterSection>
                    ))}
        </div>
    )
}

FilterSections.propTypes = {
    /** Extra classes */
    extraClasses: PropTypes.string,
}

FilterSections.defaultProps = {
    extraClasses: '',
}

export default FilterSections
